import { inject, Injectable, signal } from "@angular/core";
import { CipherService } from "@bitwarden/common/abstractions/cipher.service";
import { VaultReader } from "@bitwarden/web-vault/app/services/dali/vault.reader";
import { StoreModel } from "@bitwarden/web-vault/app/services/dali/vault-parser/parser.type";
import {
  StoreModelNames,
  StoreVaultCodeByModelName,
} from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { VaultWriter } from "@bitwarden/web-vault/app/services/dali/vault.writer";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { createMutex } from "@bitwarden/web-vault/app/shared/utils/mutex";

@Injectable({
  providedIn: "root",
})
export class DaliService {
  protected appState = inject(AppStateStoreService);
  protected cipherService = inject(CipherService);

  private vaultLoader: VaultReader = new VaultReader();
  private vaultWriter: VaultWriter = new VaultWriter();

  /** Serializes every vault write so two saves can't interleave. See {@link createMutex}. */
  private readonly runExclusive = createMutex();

  /**
   * Bumped whenever an account finishes being shared into a collection — sharing a
   * *already org-owned* cipher into another collection goes straight through
   * `cipherService.shareWithServer`, invisible to `userStore.enrichAccounts`'s reactive vault
   * store, so nothing else tells a "who's this shared with" view (e.g.
   * AccountLinkedCollectionComponent) that it's stale. Callers that only care about a fresh
   * fork (which *does* flow through the vault store) can ignore this.
   */
  readonly sharedVersion = signal(0);

  notifyShared() {
    this.sharedVersion.update((v) => v + 1);
  }

  async initialise() {
    return await this.vaultLoader.loadVault();
  }

  async save(
    type: StoreModelNames,
    models: StoreModel[],
    organizationId?: string,
    collectionIds?: string[],
  ) {
    return await this.runExclusive(async () => {
      this.appState.startProcess("syncing-data", "Saving data...");

      try {
        const result = await this.vaultWriter.syncToVault(
          type,
          models,
          organizationId,
          collectionIds,
        );

        return result.get(StoreVaultCodeByModelName[type]) ?? [];
      } finally {
        this.appState.endProcess("syncing-data");
      }
    });
  }

  /**
   * Adds a collection to an already org-shared cipher without re-creating it, so any view can
   * share an existing item into another collection without duplicating the cipher.
   *
   * Returns whether the cipher was actually shared — `false` when it was already in
   * `collectionId`, so callers can tell a real share apart from a no-op.
   */
  async share(vaultId: string, organizationId: string, collectionId: string): Promise<boolean> {
    return await this.runExclusive(async () => {
      const cipher = await this.cipherService.get(vaultId);
      const cipherView = await cipher.decrypt();
      const existingCollectionIds = cipherView.collectionIds ?? [];
      if (existingCollectionIds.includes(collectionId)) {
        return false;
      }

      const collectionIds = Array.from(new Set([...existingCollectionIds, collectionId]));
      await this.cipherService.shareWithServer(cipherView, organizationId, collectionIds);
      return true;
    });
  }

  /**
   * Returns the collection ids a cipher is currently shared into, so callers can tell which
   * teams an account is already shared with. `collectionIds` is stored unencrypted on the domain
   * `Cipher`, so this skips the decrypt (which can also fail if the org key isn't loaded yet).
   */
  async getCollectionIds(vaultId: string): Promise<string[]> {
    return (await this.cipherService.get(vaultId))?.collectionIds ?? [];
  }

  async commit(type: StoreModelNames, models: StoreModel[]) {
    return await this.runExclusive(async () => {
      return await this.vaultWriter.syncToVault(type, models);
    });
  }
}
